import {
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
  } from '@tanstack/react-table'
  import React, { useEffect, useMemo, useRef } from 'react'
  import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './Table'
  import { useState } from 'react'
import { Button } from 'react-bootstrap'
  
export default function DataTableLayerCustom({ 
  data, 
  columns, 
  title, 
  onClick , 
  btnTitle, 
  className,
  showButton = true,
  // NEW:
  filterConfigs = [],      // [{ key:'year', label:'Year', deriveFrom:{type:'year', source:'time'} }, { key:'channel', label:'Channel', column:'channel_name' }]
  onFilterChange,          // (filters) => void   // optional (untuk server fetch / download)
  onDownload,              // (filters) => void   // optional (untuk tombol Download)
  customFilterContent
}) {
  const [sorting, setSorting] = useState([]);
  const [filtering, setFiltering] = useState('');
  const [filters, setFilters] = useState({}); // NEW

  // helper unik
  const unique = (arr) => [...new Set(arr.filter(v => v !== null && v !== undefined && v !== ''))];

  // === opsi dinamis untuk setiap filter (auto dari data bila tidak diberi options) ===
  const dynamicOptionsMap = useMemo(() => {
    const map = {};
    filterConfigs.forEach(cfg => {
      // 1) jika options statis disediakan, pakai itu
      if (Array.isArray(cfg.options) && cfg.options.length) {
        map[cfg.key] = cfg.options.map(v => String(v));
        return;
      }
      // 2) derive year dari kolom tanggal
      if (cfg.deriveFrom?.type === 'year' && cfg.deriveFrom?.source) {
        const years = unique(
          (data || []).map(r => {
            const raw = r?.[cfg.deriveFrom.source];
            const d = raw ? new Date(raw) : null;
            const y = d && !isNaN(d) ? d.getFullYear() : null;
            return y;
          })
        ).sort((a,b) => b - a).map(y => String(y));
        map[cfg.key] = years;
        return;
      }
      // 3) ambil unik dari kolom tertentu
      if (cfg.column) {
        const vals = unique((data || []).map(r => r?.[cfg.column] ?? ''));
        map[cfg.key] = vals.map(v => String(v));
      }
    });
    return map;
  }, [data, filterConfigs]);

const onFilterChangeRef = useRef(onFilterChange);
  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);
  useEffect(() => {
    onFilterChangeRef.current && onFilterChangeRef.current(filters);
  }, [filters]);

  const handleChangeFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // TanStack tetap pakai data yang sudah terfilter
  const table = useReactTable({
    data,// ← pakai hasil filter client
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      globalFilter: filtering,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setFiltering,
  });

  const rows = table.getRowModel() ? table.getRowModel().rows : [];
  const isNoData = rows.length === 0;



  return (
    <div className="card basic-data-table">
      <div className="card-header">
        <h5 className="card-title mb-0">{title}</h5>
      </div>

      <div className="card-body">
        {/* Toolbar: search + dynamic filters + buttons */}
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">

          {/* kiri: search + filters */}
          <div className="d-flex align-items-center justify-between gap-2">
            <input
              type="text"
              value={filtering}
              onChange={(e) => setFiltering(e.target.value)}
              className="form-control form-control-sm"
              placeholder="Search..."
              style={{ minWidth: 220 }}
            />

            {/* render filter select dinamis */}
            {filterConfigs.map(cfg => {
              const opts = dynamicOptionsMap[cfg.key] || [];
              return (
                <select
                  key={cfg.key}
                  className="form-select form-select-sm"
                  value={filters[cfg.key] ?? 'ALL'}
                  onChange={(e) => handleChangeFilter(cfg.key, e.target.value)}
                >
                  <option value="ALL">All {cfg.label}</option>
                  {opts.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
              );
            })}
          </div>

          {/* kanan: aksi (optional download + tombol custom) */}
          <div className="d-flex align-items-center gap-2">
            {typeof onDownload === 'function' && (
              <Button variant="outline-secondary" size="sm" onClick={() => onDownload(filters)}>
                Download
              </Button>
            )}
            {showButton && (
              <Button onClick={onClick} className={className}>
                {btnTitle}
              </Button>
            )}
          </div>
          { customFilterContent }
        </div>

        {/* Table */}
        {isNoData ? (
          <Table className="table table-striped table-bordered table-sm">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && <span>{header.column.getIsSorted() === 'asc' ? '🔼' : '🔽'}</span>}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              <TableRow className="bg-light">
                <TableCell colSpan={columns.length} className="text-center">
                  <p className='text-center py-5 fs-4 text-danger'>Data Not Available</p>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : (
          <Table className="table table-striped table-bordered table-sm">
            <TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead key={header.id}>
                      {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getIsSorted() && <span>{header.column.getIsSorted() === 'asc' ? '🔼' : '🔽'}</span>}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-5">
          <div className="justify-content-between d-flex gap-3">
            <button className="btn btn-outline-dark btn-sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              Previous Page
            </button>
            <button className="btn btn-outline-dark btn-sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              Next Page
            </button>
          </div>
          <div>
            <span className="me-2">
              Page {table.getState().pagination.pageIndex + 1} of {Math.ceil(table.getRowCount() / table.getState().pagination.pageSize)}
            </span>
            <select
              value={table.getState().pagination.pageSize}
              onChange={(e) => {
                const newPageSize = Number(e.target.value);
                table.setPageSize(newPageSize);
                table.setState(prev => ({ ...prev, pagination: { ...prev.pagination, pageSize: newPageSize } }));
              }}
              className="form-select form-select-sm d-inline-block w-auto ms-2"
            >
              {[10, 20, 30, 40, 50, 100].map((pageSize) => (
                <option key={pageSize} value={pageSize}>Show {pageSize}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}

  